import React from 'react';
import { Group, NumberInput, SegmentedControl, Select, Stack, Text } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { useTranslation } from 'react-i18next';
import {
    ConfigFormValues,
    TREND_TYPE_ABOVE_AVERAGE,
    TREND_TYPE_CONSECUTIVE_INCREASE,
    TREND_TYPE_SUDDEN_INCREASE,
} from './alertConfigurationConstants.ts';

interface Props {
    form: UseFormReturnType<ConfigFormValues>;
    categoryOptions?: { value: string; label: string }[];
    showCategory?: boolean;
}

const TrendFields: React.FC<Props> = ({ form, categoryOptions = [], showCategory = true }) => {
    const { t } = useTranslation();
    const trendType = form.values.trendType;

    const descriptionKey =
        trendType === TREND_TYPE_CONSECUTIVE_INCREASE ? 'alertRule.trendConfig.consecutiveIncreaseDescription'
        : trendType === TREND_TYPE_ABOVE_AVERAGE ? 'alertRule.trendConfig.aboveAverageDescription'
        : 'alertRule.trendConfig.suddenIncreaseDescription';

    return (
        <Stack gap="md">
            {showCategory && (
                <Select
                    label={t('alertRule.form.category')}
                    placeholder={t('alertRule.form.categoryPlaceholder')}
                    data={categoryOptions}
                    searchable
                    {...form.getInputProps('conditionCategory')}
                />
            )}

            <Stack gap={4}>
                <Text size="sm" fw={500}>{t('alertRule.trendConfig.trendTypeLabel')}</Text>
                <SegmentedControl
                    fullWidth
                    data={[
                        { value: TREND_TYPE_SUDDEN_INCREASE, label: t('alertRule.trendConfig.suddenIncrease') },
                        { value: TREND_TYPE_CONSECUTIVE_INCREASE, label: t('alertRule.trendConfig.consecutiveIncrease') },
                        { value: TREND_TYPE_ABOVE_AVERAGE, label: t('alertRule.trendConfig.aboveAverage') },
                    ]}
                    value={trendType}
                    onChange={(val) => form.setFieldValue('trendType', val)}
                />
                <Text size="xs" c="dimmed">{t(descriptionKey)}</Text>
            </Stack>

            {trendType === TREND_TYPE_SUDDEN_INCREASE && (
                <NumberInput
                    label={t('alertRule.trendConfig.deltaThreshold')}
                    min={1}
                    max={10}
                    decimalScale={1}
                    {...form.getInputProps('trendDeltaThreshold')}
                />
            )}

            {trendType === TREND_TYPE_CONSECUTIVE_INCREASE && (
                <NumberInput
                    label={t('alertRule.trendConfig.minConsecutiveIncreases')}
                    min={2}
                    max={10}
                    {...form.getInputProps('trendMinConsecutiveIncreases')}
                />
            )}

            {trendType === TREND_TYPE_ABOVE_AVERAGE && (
                <Group grow>
                    <NumberInput
                        label={t('alertRule.trendConfig.meanDeltaThreshold')}
                        min={1}
                        max={10}
                        decimalScale={1}
                        {...form.getInputProps('trendMeanDeltaThreshold')}
                    />
                    <NumberInput
                        label={t('alertRule.trendConfig.windowSize')}
                        min={2}
                        max={30}
                        {...form.getInputProps('trendWindowSize')}
                    />
                </Group>
            )}

            <NumberInput
                label={t('alertRule.trendConfig.minCurrentScore')}
                min={0}
                max={100}
                decimalScale={1}
                {...form.getInputProps('trendMinCurrentScore')}
            />

            <Select
                label={t('alertRule.trendConfig.missingDataPolicy')}
                data={[
                    { value: 'skip', label: t('alertRule.trendConfig.missingDataPolicySkip') },
                    { value: 'ignore_gaps', label: t('alertRule.trendConfig.missingDataPolicyIgnoreGaps') },
                ]}
                {...form.getInputProps('trendMissingDataPolicy')}
            />
        </Stack>
    );
};

export default TrendFields;
